/* The pack is committed as ONE /cart/add.js POST with an items array, then bridged to Horizon with CartLinesUpdateEvent. */

import { formatMoney } from '@theme/money-formatting';

const CONFIG_DEFAULTS = {
  moneyFormat: '£{{amount}}',
  currency: 'GBP',
  heading: 'Your Pack',
  emptyText: '',
  completeLine: '',
  addLabel: 'Add Pack To Cart',
  accessoryAddLabel: '+ Add',
  accessoryAddedLabel: 'Added',
  removeLabel: 'Remove from pack',
  pairSingular: 'Pair',
  pairPlural: 'Pairs',
  tiers: [],
};

/* Opening the drawer after an add is ours to do: Horizon only auto-opens under the editor-owned `auto_open_cart_drawer` setting. Mirrors bronte-pdp-main.js. */
async function openCartDrawer() {
  try {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    if (!customElements.get('theme-drawer')) {
      await customElements.whenDefined('theme-drawer');
    }

    if (typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  } catch (error) {
    // A drawer that will not open must never turn a completed add into a failed one.
  }
}

class BrontePackBuilder {
  constructor(root) {
    this.root = root;
    this.config = this.#readConfig();
    /* Sorted and de-duplicated: two tiers at the same pair count would otherwise disagree with the tracker. */
    this.tiers = (this.config.tiers || [])
      .filter((tier) => tier && Number(tier.pairs) > 0)
      .map((tier) => ({
        pairs: Number(tier.pairs),
        saving: Number(tier.saving) || 0,
        nudge: tier.nudge || '',
      }))
      .sort((a, b) => a.pairs - b.pairs)
      .filter((tier, index, all) => index === 0 || tier.pairs !== all[index - 1].pairs);

    this.cards = Array.from(root.querySelectorAll('[data-byp-card]'));
    this.accessories = Array.from(root.querySelectorAll('[data-byp-accessory]'));
    /* Two rails, desktop proportional and mobile equal-gap: both kept in step so a breakpoint change never shows a stale track. */
    this.bars = root.querySelectorAll('[data-bronte-progress]');
    this.slots = root.querySelector('[data-byp-slots]');
    this.nudge = root.querySelector('[data-byp-nudge]');
    this.headingDetail = root.querySelector('[data-byp-heading-detail]');
    this.submit = root.querySelector('[data-byp-submit]');
    this.submitLabel = root.querySelector('[data-byp-submit-label]');
    this.error = root.querySelector('[data-byp-error]');

    /* Pack contents live on the cards (`data-qty`), so the steppers, the summary and the cart POST read one source of truth. */
    this.pickedAccessories = [];

    this.#bind();
    this.render();
    this.#prefillFromQuery();
  }

  /* Contract with the `In class with us` section: its "Shop the item" link points here as
     /pages/build-your-pack?item={product-handle}. An unknown or sold-out handle is ignored. */
  #prefillFromQuery() {
    let handle = '';

    try {
      handle = new URLSearchParams(window.location.search).get('item') || '';
    } catch (error) {
      return;
    }

    if (!handle) return;

    const card = this.cards.find((item) => item.dataset.handle === handle);
    if (!card || card.dataset.unavailable === 'true') return;
    if ((Number(card.dataset.qty) || 0) > 0) return;

    this.stepPair(card, 1);
  }

  #readConfig() {
    const node = this.root.querySelector('[data-byp-config]');
    if (!node) return { ...CONFIG_DEFAULTS };

    try {
      return { ...CONFIG_DEFAULTS, ...JSON.parse(node.textContent || '{}') };
    } catch (error) {
      console.warn('[bronte-byp] config parse failed', error);
      return { ...CONFIG_DEFAULTS };
    }
  }

  #bind() {
    this.cards.forEach((card) => {
      card.querySelectorAll('[data-byp-step]').forEach((step) => {
        step.addEventListener('click', () => this.stepPair(card, Number(step.dataset.bypStep) || 0));
      });
    });

    this.accessories.forEach((accessory) => {
      const toggle = accessory.querySelector('[data-byp-acc-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.toggleAccessory(accessory));
    });

    if (this.slots) {
      this.slots.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-byp-remove]') : null;
        if (!button) return;
        const card = this.cards.find((item) => item.dataset.variantId === button.dataset.removeId);
        if (card) this.stepPair(card, -1);
      });
    }

    if (this.submit) this.submit.addEventListener('click', () => this.addPackToCart());
  }

  #money(value) {
    return formatMoney(value, this.config.moneyFormat, this.config.currency);
  }

  #itemOf(element) {
    return {
      element,
      variantId: element.dataset.variantId,
      price: Number(element.dataset.price) || 0,
      title: element.dataset.title || '',
    };
  }

  get lines() {
    return this.cards
      .map((card) => ({ ...this.#itemOf(card), qty: Number(card.dataset.qty) || 0 }))
      .filter((line) => line.qty > 0);
  }

  get pairCount() {
    return this.lines.reduce((sum, line) => sum + line.qty, 0);
  }

  /* Quantities are open-ended: the tiers set the bar's top milestone, not a ceiling. */
  stepPair(card, delta) {
    if (!card || card.dataset.unavailable === 'true' || !delta) return;

    const current = Number(card.dataset.qty) || 0;
    const next = Math.max(0, current + delta);
    if (next === current) return;

    card.dataset.qty = String(next);
    card.dataset.selected = next > 0 ? 'true' : 'false';

    const value = card.querySelector('[data-byp-step-value]');
    if (value) value.textContent = String(next);

    this.render();
  }

  toggleAccessory(accessory) {
    const index = this.pickedAccessories.findIndex((item) => item.element === accessory);

    if (index > -1) {
      this.pickedAccessories.splice(index, 1);
      accessory.dataset.selected = 'false';
    } else {
      this.pickedAccessories.push(this.#itemOf(accessory));
      accessory.dataset.selected = 'true';
    }

    const toggle = accessory.querySelector('[data-byp-acc-toggle]');
    const label = accessory.querySelector('[data-byp-acc-label]');
    const isSelected = accessory.dataset.selected === 'true';
    if (toggle) toggle.setAttribute('aria-pressed', String(isSelected));
    if (label) {
      label.textContent = isSelected ? this.config.accessoryAddedLabel : this.config.accessoryAddLabel;
    }

    this.render();
  }

  get currentTier() {
    const pairs = this.pairCount;
    let reached = null;
    this.tiers.forEach((tier) => {
      if (pairs >= tier.pairs) reached = tier;
    });
    return reached;
  }

  get nextTier() {
    const pairs = this.pairCount;
    return this.tiers.find((tier) => pairs < tier.pairs) || null;
  }

  get discount() {
    const tier = this.currentTier;
    return tier ? Math.round(tier.saving * 100) : 0;
  }

  get total() {
    const pairs = this.lines.reduce((sum, line) => sum + line.price * line.qty, 0);
    const extras = this.pickedAccessories.reduce((sum, item) => sum + item.price, 0);
    return Math.max(0, pairs - this.discount) + extras;
  }

  render() {
    const count = this.pairCount;
    const total = this.total;

    if (this.headingDetail) {
      if (count === 0) {
        this.headingDetail.textContent = '';
      } else {
        const noun = count === 1 ? this.config.pairSingular : this.config.pairPlural;
        const tick = this.discount > 0 ? ' ✓' : '';
        this.headingDetail.textContent = `: ${count} ${noun} · ${this.#money(total)}${tick}`;
      }
    }

    this.#renderSlots();

    if (this.nudge) {
      const next = this.nextTier;
      this.nudge.textContent = next ? next.nudge : this.config.completeLine;
    }

    if (this.bars.length && window.BronteProgressBar) {
      window.BronteProgressBar.update(this.bars, count);
    }

    if (this.submit) {
      this.submit.disabled = count === 0;
      if (this.submitLabel) {
        this.submitLabel.textContent =
          count === 0 ? this.config.addLabel : `${this.config.addLabel}: ${this.#money(total)}`;
      }
    }
  }

  #renderSlots() {
    if (!this.slots) return;

    if (this.pairCount === 0) {
      this.slots.innerHTML = '';
      if (!this.config.emptyText) return;
      const empty = document.createElement('p');
      empty.className = 'bronte-byp__empty';
      empty.textContent = this.config.emptyText;
      this.slots.appendChild(empty);
      return;
    }

    const rows = document.createDocumentFragment();
    let slot = 0;

    /* One row per PAIR, not per style: a style taken twice fills two slots and its remove gives one back. */
    this.lines.forEach((line) => {
      for (let copy = 0; copy < line.qty; copy += 1) {
        slot += 1;

        const row = document.createElement('div');
        row.className = 'bronte-byp__slot';

        const label = document.createElement('span');
        label.textContent = `${slot} · ${line.title} · ${this.#money(line.price)}`;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bronte-byp__slot-remove';
        remove.setAttribute('data-byp-remove', '');
        remove.dataset.removeId = line.variantId;
        remove.setAttribute('aria-label', `${this.config.removeLabel}: ${line.title}`);
        remove.textContent = '✕';

        row.append(label, remove);
        rows.appendChild(row);
      }
    });

    this.slots.innerHTML = '';
    this.slots.appendChild(rows);
  }

  #setError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.hidden = !message;
  }

  async addPackToCart() {
    if (this.pairCount === 0 || !this.submit) return;

    /* `_bundle` is a hidden line item property, carried onto the order and the marker the Bundle Manager app filters on. */
    const packMarker = `pack-${this.pairCount}`;

    const lines = [
      ...this.lines.map((line) => ({
        id: Number(line.variantId),
        quantity: line.qty,
        properties: { _bundle: packMarker },
      })),
      ...this.pickedAccessories.map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
        properties: { _bundle: 'accessory' },
      })),
    ];

    this.#setError('');
    this.submit.dataset.loading = 'true';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    let events = null;
    let deferred = null;

    try {
      events = await import('@shopify/events');
      deferred = events.CartLinesUpdateEvent.createPromise();
      this.root.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[bronte-byp] standard events unavailable', error);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      }).then((result) => result.json());

      if (response.status) {
        this.#setError(response.description || response.message || 'Something went wrong adding your pack.');
        deferred?.reject(new Error(response.message || 'add failed'));
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((result) => result.json());

      if (deferred && events) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'bronte-byp-picker-summary',
            sourceId: this.root.dataset.sectionId,
            itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
            sections: response.sections,
            didError: false,
          },
        });

        await openCartDrawer();
      } else {
        window.location.href = Theme.routes.cart_url;
      }
    } catch (error) {
      console.error('[bronte-byp] add to cart failed', error);
      this.#setError('Something went wrong adding your pack. Please try again.');
      deferred?.reject(error);
    } finally {
      delete this.submit.dataset.loading;
    }
  }
}

function init(scope) {
  scope.querySelectorAll('[data-bronte-byp]').forEach((root) => {
    if (root.dataset.bypReady === 'true') return;
    root.dataset.bypReady = 'true';
    new BrontePackBuilder(root);
  });
}

init(document);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
